import React, { useState, useEffect } from 'react';

import styles from './AdminDemoPage.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Avatar, ButtonGroup, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { useFormik } from 'formik';
import Chip from '../../../components/chips/Chip/Chip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { toast } from 'react-toastify';

import Button from '../../../components/button/Button';
import DemoService from '../../../services/api/DemoService';
import { DemoStatus, DemoStatusMap } from '../../../constants/demo.constants';
import UserProfileItem from '../../../components/user-profile/UserProfileItem';
import UserProfileWrapper from '../../../components/user-profile/UserProfileWrapper';

export default function AdminDemoPage() {
  const [demo, setDemo] = useState({});
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const anchorRef = React.useRef(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const params = useParams();

  useEffect(() => {
    const fetchInitialData = async () => {
      const { body } = await DemoService.getDemo(params.demoId);
      setDemo(body);
    };
    params.demoId && fetchInitialData();
  }, []);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (_, index) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const options = [
    { name: DemoStatus.SCHEDULED, label: 'Schedule' },
    { name: DemoStatus.COMPLETED, label: 'Complete' },
    { name: DemoStatus.CANCELLED, label: 'Cancell' },
  ];

  const formik = useFormik({
    initialValues: { notes: '', status: '' },
    onSubmit: async (values, { resetForm }) => {
      await handleStatusUpdate({
        notes: values.notes || undefined,
        status: options[selectedIndex].name,
      });

      toast.success('Demo was successfully updated');

      resetForm();
    },
  });

  const handleStatusUpdate = async (updates) => {
    await DemoService.updateDemo(demo.id, updates);

    setDemo({ ...demo, status: updates.status, notes: updates.notes });
  };

  const handleDelete = async () => {
    await DemoService.deleteDemo(demo.id);
    toast.success('Demo was successfully deleted');

    navigate('/internal/admin/demos');
  };

  const displayDemoRequestActions = () => {
    return (
      <form className={styles.formWrapper} onSubmit={formik.handleSubmit}>
        <Button
          type=""
          className={styles.deleteButton}
          onClick={(e) => {
            e.preventDefault();
            handleDelete();
          }}
        >
          Delete
        </Button>
        <ButtonGroup className={styles.buttonGroup} variant="contained" aria-label="split button">
          <Button
            loading={formik.isSubmitting}
            variant="contained"
            type="submit"
            className={styles.actionButton}
            refAnchor={anchorRef}
          >
            {options[selectedIndex].label}
          </Button>
          <Button
            className={styles.optionsButton}
            type="none"
            disabled={formik.isSubmitting}
            onClick={(e) => {
              e.preventDefault();
              setOpen((prevOpen) => !prevOpen);
            }}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>

        <Popper
          sx={{ zIndex: 1, width: 'max-content' }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom',
                width: anchorRef.current ? anchorRef.current.offsetWidth : null,
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    {options.map((option, index) => (
                      <MenuItem
                        key={option.name}
                        selected={index === selectedIndex}
                        onClick={(event) => handleMenuItemClick(event, index)}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </form>
    );
  };

  const demoStatus = DemoStatusMap[demo.status];
  return (
    <Box component="main" className={styles.wrapper}>
      <UserProfileWrapper
        title={
          <div className={styles.header}>
            <IconButton onClick={() => navigate(`/internal/admin/demos`)}>
              <ChevronLeftIcon />
            </IconButton>{' '}
            Requested Demo
          </div>
        }
      >
        <div className={styles.contentWrapper}>
          <UserProfileItem title="Name">
            <div className={styles.profileWrapper}>
              <div className={styles.profile}>
                <Avatar>
                  {demo?.firstName && demo?.firstName[0]}
                  {demo?.lastName && demo?.lastName[0]}
                </Avatar>
                <Typography>
                  {demo.firstName} {demo.lastName}
                </Typography>
              </div>
              {displayDemoRequestActions()}
            </div>
          </UserProfileItem>

          <UserProfileItem title="Status">
            <div className={styles.item}>
              {demoStatus && <Chip sx={{ textAlign: 'left' }} color={demoStatus.color} label={demoStatus.label} />}
            </div>
          </UserProfileItem>

          <UserProfileItem title="Created at">
            <div className={styles.item}>{dayjs(demo?.createdAt).format('MMM DD YYYY H:mm A') || 'N/A'}</div>
          </UserProfileItem>

          <UserProfileItem title="Email address">
            <div className={styles.item}>{demo.email}</div>
          </UserProfileItem>
          <UserProfileItem title="Phone number">
            <div className={styles.item}>{demo.phoneNumber}</div>
          </UserProfileItem>

          <UserProfileItem title="Company">
            <div className={styles.item}>{demo.company}</div>
          </UserProfileItem>

          <UserProfileItem title="Role">
            <div className={styles.item}>{demo.role}</div>
          </UserProfileItem>

          <UserProfileItem title="Heard abous us from">
            <div className={styles.item}>{demo.hearFrom}</div>
          </UserProfileItem>
        </div>
      </UserProfileWrapper>
    </Box>
  );
}
